import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Briefcase, MapPin, TrendingUp, ExternalLink, CheckCircle2, Clock, Building,
  ChevronDown, Inbox, Award,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

// Openings come from the admin panel (placements table) via
// get-course-dashboard.php → course.placements; the student's own status from
// user_placements → course.placement_status (null = no record yet).

const LOGO_COLORS = [
  'bg-blue-100 text-blue-600', 'bg-purple-100 text-purple-600', 'bg-emerald-100 text-emerald-600',
  'bg-primary/20 text-primary', 'bg-rose-100 text-rose-600', 'bg-sky-100 text-sky-600',
];
const logoColor = (name = '') =>
  LOGO_COLORS[[...name].reduce((sum, ch) => sum + ch.charCodeAt(0), 0) % LOGO_COLORS.length];

const todayISO = () => new Date().toISOString().slice(0, 10);
const isClosed = (job) => !!job.deadline && job.deadline < todayISO();

const fmtDate = (d) => d
  ? new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
  : null;

const STATUS_COPY = {
  placed:      { label: 'Placed', tone: 'bg-green-100 text-green-700', icon: Award },
  in_progress: { label: 'In progress', tone: 'bg-primary/15 text-primary', icon: Clock },
  not_placed:  { label: 'Not placed yet', tone: 'bg-muted text-muted-foreground', icon: Briefcase },
};

export default function PlacementSection({ course }) {
  const jobs = course?.placements || [];
  const status = course?.placement_status;
  const [expanded, setExpanded] = useState(null);

  const openJobs = jobs.filter(j => !isClosed(j));
  const closedJobs = jobs.filter(isClosed);
  const companies = new Set(jobs.map(j => j.company_name?.trim().toLowerCase()).filter(Boolean)).size;

  const statusKey = (status?.placement_status || 'not_placed').toLowerCase().replace(/[\s-]+/g, '_');
  const statusInfo = STATUS_COPY[statusKey] || { label: status?.placement_status, tone: 'bg-muted text-muted-foreground', icon: Briefcase };

  const stats = [
    { label: 'Open Roles', value: openJobs.length, icon: Briefcase, color: 'text-primary' },
    { label: 'Companies', value: companies, icon: Building, color: 'text-blue-500' },
    { label: 'Your Status', value: statusInfo.label, icon: statusInfo.icon, color: statusKey === 'placed' ? 'text-emerald-500' : 'text-muted-foreground' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-heading text-2xl font-bold tracking-tight">Placement Support</h2>
        <p className="text-muted-foreground text-sm mt-1">Opportunities shared by your course team</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {stats.map((s, i) => (
          <motion.div key={s.label} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07 }}
            className="glass rounded-2xl border border-white/80 p-4 text-center">
            <s.icon className={`w-6 h-6 mx-auto mb-2 ${s.color}`} />
            <p className={`font-heading font-black ${typeof s.value === 'number' ? 'text-xl' : 'text-sm sm:text-base leading-7'}`}>{s.value}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{s.label}</p>
          </motion.div>
        ))}
      </div>

      {/* Placed banner — only when the team has recorded a placement */}
      {statusKey === 'placed' && (status?.company_name || status?.salary) && (
        <div className="glass rounded-2xl border border-green-200 bg-green-50/60 p-5 flex items-center gap-4">
          <div className="w-11 h-11 rounded-2xl bg-green-100 flex items-center justify-center flex-shrink-0">
            <Award className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <p className="font-heading font-bold text-sm text-green-800">Congratulations on your placement!</p>
            <p className="text-xs text-green-700 mt-0.5">
              {[status.company_name, status.salary].filter(Boolean).join(' · ')}
            </p>
          </div>
        </div>
      )}

      {/* Openings */}
      <div>
        <h3 className="font-heading font-bold text-base mb-4">
          Live Opportunities {openJobs.length > 0 && <span className="text-muted-foreground font-medium">({openJobs.length})</span>}
        </h3>

        {jobs.length === 0 ? (
          <div className="glass rounded-2xl border border-white/80 p-10 text-center">
            <div className="w-12 h-12 rounded-2xl bg-primary/15 flex items-center justify-center mx-auto mb-3">
              <Inbox className="w-6 h-6 text-primary" />
            </div>
            <p className="font-heading font-bold text-sm">No openings posted yet</p>
            <p className="text-xs text-muted-foreground mt-1">New opportunities for your course will show up here as soon as they're shared.</p>
          </div>
        ) : (
          <div className="grid gap-3">
            {openJobs.length === 0 && (
              <p className="text-sm text-muted-foreground glass rounded-2xl border border-white/80 p-4">
                All current openings have closed — check back soon for new ones.
              </p>
            )}
            {[...openJobs, ...closedJobs].map((job, i) => (
              <JobCard
                key={job.id}
                job={job}
                index={i}
                closed={isClosed(job)}
                open={expanded === job.id}
                onToggle={() => setExpanded(expanded === job.id ? null : job.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function JobCard({ job, index, closed, open, onToggle }) {
  return (
    <motion.div initial={{ opacity: 0, x: -14 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.06 + 0.2 }}
      className={`glass rounded-2xl border border-white/80 overflow-hidden transition-colors ${closed ? 'opacity-60' : 'hover:border-primary/20'}`}>
      <div className="p-4 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <div className={`w-11 h-11 rounded-xl ${logoColor(job.company_name)} flex items-center justify-center font-heading font-black text-lg flex-shrink-0`}>
          {(job.company_name || '?').trim()[0]?.toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-heading font-bold text-sm">
            {job.job_title || 'Open role'} <span className="text-muted-foreground font-medium">at {job.company_name}</span>
            {closed && <span className="ml-2 text-[10px] font-bold px-2 py-0.5 rounded-full bg-muted text-muted-foreground align-middle">Closed</span>}
          </p>
          <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-xs text-muted-foreground">
            {job.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3 text-primary" />{job.location}</span>}
            {job.salary && <span className="flex items-center gap-1"><TrendingUp className="w-3 h-3 text-green-500" /><span className="text-green-600 font-semibold">{job.salary}</span></span>}
            {job.deadline && <span className="flex items-center gap-1"><Clock className="w-3 h-3 text-primary" />{closed ? 'Closed' : 'Apply by'} {fmtDate(job.deadline)}</span>}
            {job.posted_date && <span>Posted {fmtDate(job.posted_date)}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {job.job_description && (
            <Button size="sm" variant="outline" onClick={onToggle} aria-expanded={open}
              className="rounded-xl text-xs font-semibold border-black/15 gap-1">
              Details <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
            </Button>
          )}
          {job.apply_link && !closed ? (
            <a href={job.apply_link} target="_blank" rel="noopener noreferrer">
              <Button size="sm" className="rounded-xl text-xs font-semibold bg-primary text-primary-foreground shadow-sm shadow-primary/25 hover:bg-primary/90 gap-1.5">
                Apply Now <ExternalLink className="w-3 h-3" />
              </Button>
            </a>
          ) : !closed && (
            <span className="text-[11px] text-muted-foreground">Apply link coming soon</span>
          )}
        </div>
      </div>
      <AnimatePresence initial={false}>
        {open && job.job_description && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}>
            <div className="px-4 pb-4 sm:pl-[4.75rem] text-sm text-muted-foreground leading-relaxed whitespace-pre-line border-t border-black/5 pt-3">
              {job.job_description}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
